<template>
    <div>
        <div class="modal-content-wrapper px-3 my-2">
            <p v-if="this.limitError">{{ this.limitError }}</p>
            <div v-else-if="showContactInfo" class="fail-to-receive-explanation">
                <p v-html="$t('sms.fail_to_receive_message')"></p>
            </div>
            <div v-else>
                <div v-if="!codeExpiredAt" class="explanation my-2"> {{$t('explanation')}} </div>
                <div v-if="codeExpiredAt" class="explanation">{{$t('sent_text')}}</div>
                <loading-indicator v-if="isSending" :is-loading="true" :full-parent="false" :clearBackground="true" />
                <input
                    ref="input"
                    v-if="!isSending"
                    cy-input="sms_code"
                    @change="$emit('code-change', code)"
                    v-model="code" :placeholder="$t('please_enter_sms_code')" class="form-control form-control-sm code-input" />
                <div class="d-flex mt-1 justify-content-between">
                    <div>
                        <a @click="showContactInfo = true" class="text-secondary" href="javascript:void(0);"><small>
                            {{$t('cannot_receive_code')}}
                        </small></a>
                    </div>
                    <div v-if="codeExpiredAt" class="text-right">
                        <div v-if="!freezeCounterToken">
                            <a @click="doGetSMSCode" class="text-secondary" href="javascript:void(0);"><small>{{$t('resend')}}</small></a>
                        </div>
                        <a cy-link="change_number" @click="$emit('change-number')" class="text-secondary" href="javascript:void(0);"><small>{{$t('change_number')}}</small></a>
                        <br>
                    </div>
                </div>
                <div class="explanation text-secondary my-2">{{ $t('receive_code_note') }}</div>
                <div class="explanation text-secondary my-2" v-html="$t('receive_code_note_2')"></div>
                <div v-if="freezeCounterToken && freezeCounter > 0" class="explanation text-xs text-right text-muted flex">
                    <div>{{$t('n_seconds_left_to_resend', {seconds: freezeCounter})}}</div>
                </div>

                <div class="text-danger mt-4" v-if="errors"><small v-html="errors"></small></div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '../../mixins/api';
import moment from 'moment';
import vuePhoneInput from '../../components/vue-phone-input/index.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import { EventRequestSmsVerification } from '../../bu/models/GA4';

export default {
    components: { vuePhoneInput, LoadingIndicator },
    mixins: [api],
    props: {
        phoneCallingCode: { required: true, type: String },
        phoneNumber: { required: true, type: String },
        provider: { required: true, type: Object },
    },
    emits: ['onReachDeadEnd'],
    data() {
        return {
            isSending: false,
            code: '',
            freezeCounterToken: null,
            freezeCounter: 0,
            hasPhoneNumberUpdated: false,
            errors: '',
            showContactInfo: false,
            codeExpiredAt: null,
            limitError: '',
        }
    },
    computed: {
        contactInfo() {
            return this.$t('sms.fail_to_receive_message');
        }
    },
    mounted() {
        this.doGetSMSCode();
    },
    watch: {
        showContactInfo() {
            if (this.showContactInfo) {
                this.$emit('show-contact-info');
            }
        }
    },
    methods: {
        submitCode() {
            this.$emit('sms-code-input', {
                code: this.code,
                phoneNumber: this.phoneNumber,
                phoneCallingCode: this.phoneCallingCode
            });
        },
        doGetSMSCode() {
            this.errors = '';
            this.isSending = true;
            this.getSMSCode(this.phoneCallingCode, this.phoneNumber, (response, error, errors) => {
                this.isSending = false;
                this.trackRequestSmsVerification(!error);
                if (!error) {
                    this.codeExpiredAt = moment().add(10, 'minute');
                    this.freezeCounter = 60;
                    this.freezeCounterToken = setInterval(() => {
                        this.freezeCounter -= 1;
                        if (this.freezeCounter <= 0) {
                            clearInterval(this.freezeCounterToken);
                            this.freezeCounterToken = null;
                        }
                    }, 1000);
                    setTimeout(() => {
                        this.$refs['input']?.focus();
                    }, 200)
                } else if (errors) {
                    const {status} = error.response;

                    if(status === 429) {
                        this.$emit('onReachDeadEnd')
                        return this.limitError = this.$t('reached_sms_limit');
                    }

                    this.errors = errors.map((e) => this.$t(e)).join("<br>");
                }
            });
        },
        trackRequestSmsVerification(isSuccess) {
            window.tracker.track(() => new EventRequestSmsVerification('login', isSuccess));
        }
    }

}
</script>

<style scoped lang="scss">

.explanation {
    font-size: 14px;
}

.fail-to-receive-explanation {
    max-height: 70vh;
    overflow: auto;
}

</style>

<i18n>
    {
        "zh": {
            "sms_verification": "手機號碼驗證",
            "explanation": "確保聯絡暢通，請進行手機號碼驗證。",
            "sent_text": "驗證碼已發送至以上號碼，請輸入您收到的驗證碼以繼續。",
            "receive_code_note": "注意：24小時內最多可取得三次驗證碼",
            "receive_code_note_2": "<b>請確認目前的網路環境是否暢通且您的電信商是否將您的手機號碼設定為拒收廣告簡訊。</b>",
            "sending": "正在送出驗證碼...",
            "please_enter_sms_code": "請輸入六碼驗證碼",
            "n_seconds_left_to_resend": "{seconds} 秒後可重新發送",
            "resend": "重新發送驗證碼",
            "change_number": "變更號碼",
            "errors.fail_to_get_code": "目前無法取得驗證碼，請直接聯絡我們。",
            "cannot_receive_code": "無法收到驗證碼？",
            "reached_sms_limit": "已達 24 小時內可取得驗證碼的次數上限（三次）"
        },
        "en": {
            "sms_verification": "Verify Your Phone Number",
            "explanation": "For better communication, please verify your phone number.",
            "sent_text": "An verification code has been sent to below number, please enter the code received to proceed.",
            "receive_code_note": "Note: You can obtain verification codes up to three times in 24 hours",
            "receive_code_note_2": "<b>Please make sure internet connection and your service provider does not add your number to anti-ad list.</b>",
            "sending": "Sending verification code...",
            "please_enter_sms_code": "Please enter 6-digit code",
            "n_seconds_left_to_resend": "You can resend SMS code after {seconds} seconds",
            "resend": "Resend SMS code",
            "change_number": "Change Number",
            "errors.fail_to_get_code": "Fail to get SMS code temporarily, please contact us directly.",
            "cannot_receive_code": "Can't receive code?",
            "reached_sms_limit": "The maximum times for requesting verification codes in 24 hours has been exceeded"
        }
    }
</i18n>
